/**
 * ⚙️ SETTINGS SERVICE
 * Manages rental and payment settings for admin dashboard
 * Backend: https://polarride-api-17dr.vercel.app/api/admin/settings
 */

import { getApiBaseUrl } from '@/config/environment';


// ========================================
// INTERFACES
// ========================================

export type Region = 'Lagos' | 'Nairobi' | 'Accra' | 'All';

export interface RentalSettings {
  enableAddressRelease: boolean;
  addressReleaseHours: number;
  serviceSpecific?: {
    enabled: boolean;
    selfDrive: number;
    driverHire: number;
    withDriver: number;
  };
}

export interface RentalSettingsResponse {
  success: boolean;
  data?: RentalSettings;
  error?: string;
}

// Regional configuration for settings that can vary by region
export interface RegionalConfig<T> {
  regions: Region[];  // Which regions this applies to (can include 'All')
  value: T;
}

export interface PaymentSettings {
  requireSecurityDeposit: boolean;
  securityDepositType: 'percentage' | 'flat';
  securityDepositPercentage: number;
  securityDepositFlatAmount: number;
  minSecurityDeposit: number;
  maxSecurityDeposit: number;
  depositReleaseHours: number;
  enableDepositNotifications: boolean;
  bookingFee: {
    type: 'percentage' | 'flat';
    percentage: number;
    flatAmount: number;
  };
  serviceFees: {
    fleetOwner: number;
    individualHost: number;
    driver: number;
  };
  firstTimeUserDiscount: {
    enabled: boolean;
    type: 'percentage' | 'flat';
    percentage: number;
    flatAmount: number;
    applyTo: 'subtotal' | 'rentalCost';
  };
  // Regional VAT configuration
  vat: RegionalConfig<{
    enabled: boolean;
    percentage: number;
    applyTo: ('bookingFee' | 'rentalCost' | 'deliveryFee')[];
  }>[];
  // Regional delivery fee configuration
  deliveryFee: RegionalConfig<{
    enabled: boolean;
    type: 'flat' | 'perKm';
    flatAmount: number;
    perKmRate: number;
  }>[];
  eliteTierDiscounts: {
    silver: number;
    gold: number;
    platinum: number;
  };
}

export interface PaymentSettingsResponse {
  success: boolean;
  data?: PaymentSettings;
  error?: string;
}

export interface BookingPolicies {
  modificationFee: number;
  minimumModificationHours: number;
  cancellationPolicies: {
    [key: string]: {
      hoursThreshold: number;
      feePercentage: number;
      description: string;
    };
  };
}

export interface HireStandards {
  hourly: {
    minimum_hours: number;
    maximum_hours: number;
    requires_working_hours: boolean;
    description?: string;
  };
  daily: {
    minimum_days: number;
    maximum_days: number;
    requires_working_hours: boolean;
    description?: string;
  };
}

export interface HireStandardsResponse {
  success: boolean;
  data?: HireStandards;
  error?: string;
}

export interface BookingPoliciesResponse {
  success: boolean;
  data?: BookingPolicies;
  error?: string;
}

export interface BookingApprovalTimeout {
  paid_timeout_minutes: number;
  unpaid_timeout_minutes: number;
  defaults?: {
    paid_timeout_minutes: number;
    unpaid_timeout_minutes: number;
  };
  last_updated_at?: string | null;
  last_updated_by?: {
    id: string;
    name: string;
    email: string;
  } | null;
}

export interface BookingApprovalTimeoutUpdate {
  paid_timeout_minutes?: number;
  unpaid_timeout_minutes?: number;
}

export interface ApiErrorResponse {
  code: string;
  message: string;
  details?: Record<string, string> | any;
  retry_after?: number;
}

export interface BookingApprovalTimeoutResponse {
  success: boolean;
  data?: BookingApprovalTimeout;
  error?: ApiErrorResponse | string;
  message?: string;
  updated_fields?: string[];
}

// ========================================
// RENTAL SETTINGS API
// ========================================

/**
 * Get rental settings
 */
export async function getRentalSettings(): Promise<RentalSettingsResponse> {
  try {
    const token = localStorage.getItem('token');

    const response = await fetch(`${getApiBaseUrl()}/api/admin/settings/rental-settings`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    const result = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: result.message || 'Failed to load rental settings'
      };
    }

    return {
      success: true,
      data: result.data || result
    };
  } catch (error: any) {
    console.error('Error loading rental settings:', error);
    return {
      success: false,
      error: error.message || 'Network error loading rental settings'
    };
  }
}

/**
 * Update rental settings
 */
export async function updateRentalSettings(settings: RentalSettings): Promise<RentalSettingsResponse> {
  try {
    const token = localStorage.getItem('token');

    const response = await fetch(`${getApiBaseUrl()}/api/admin/settings/rental-settings`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(settings)
    });

    const result = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: result.message || 'Failed to save rental settings'
      };
    }

    return {
      success: true,
      data: result.data || result
    };
  } catch (error: any) {
    console.error('Error saving rental settings:', error);
    return {
      success: false,
      error: error.message || 'Network error saving rental settings'
    };
  }
}

// ========================================
// PAYMENT SETTINGS API
// ========================================

/**
 * Get payment settings
 */
export async function getPaymentSettings(): Promise<PaymentSettingsResponse> {
  try {
    const token = localStorage.getItem('token');

    const response = await fetch(`${getApiBaseUrl()}/api/admin/settings/payment-settings`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    const result = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: result.message || 'Failed to load payment settings'
      };
    }

    return {
      success: true,
      data: result.data || result
    };
  } catch (error: any) {
    console.error('Error loading payment settings:', error);
    return {
      success: false,
      error: error.message || 'Network error loading payment settings'
    };
  }
}

/**
 * Update payment settings
 */
export async function updatePaymentSettings(settings: PaymentSettings): Promise<PaymentSettingsResponse> {
  try {
    const token = localStorage.getItem('token');

    const response = await fetch(`${getApiBaseUrl()}/api/admin/settings/payment-settings`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(settings)
    });

    const result = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: result.message || 'Failed to save payment settings'
      };
    }

    return {
      success: true,
      data: result.data || result
    };
  } catch (error: any) {
    console.error('Error saving payment settings:', error);
    return {
      success: false,
      error: error.message || 'Network error saving payment settings'
    };
  }
}

// ========================================
// HELPER FUNCTIONS
// ========================================

/**
 * Calculate security deposit amount based on settings
 */
export function calculateDeposit(
  rentalCost: number,
  settings: PaymentSettings,
  eliteTier?: 'silver' | 'gold' | 'platinum' | null
): number {
  if (!settings.requireSecurityDeposit) {
    return 0;
  }

  let baseDeposit: number;

  if (settings.securityDepositType === 'percentage') {
    baseDeposit = rentalCost * (settings.securityDepositPercentage / 100);
  } else {
    baseDeposit = settings.securityDepositFlatAmount;
  }

  // Apply Elite tier discount
  if (eliteTier && settings.eliteTierDiscounts[eliteTier]) {
    const discount = settings.eliteTierDiscounts[eliteTier];
    baseDeposit = baseDeposit * (1 - discount / 100);
  }

  // Apply min/max limits
  baseDeposit = Math.max(settings.minSecurityDeposit, baseDeposit);
  baseDeposit = Math.min(settings.maxSecurityDeposit, baseDeposit);

  return Math.round(baseDeposit);
}

/**
 * Validate rental settings
 */
export function validateRentalSettings(settings: RentalSettings): Record<string, string> {
  const errors: Record<string, string> = {};

  if (settings.addressReleaseHours < 0 || settings.addressReleaseHours > 24) {
    errors.addressReleaseHours = 'Must be between 0 and 24 hours';
  }

  return errors;
}

/**
 * Validate payment settings
 */
export function validatePaymentSettings(settings: PaymentSettings): Record<string, string> {
  const errors: Record<string, string> = {};

  if (settings.securityDepositPercentage < 1 || settings.securityDepositPercentage > 100) {
    errors.securityDepositPercentage = 'Must be between 1% and 100%';
  }

  if (settings.securityDepositFlatAmount < 0) {
    errors.securityDepositFlatAmount = 'Must be a positive number';
  }

  if (settings.minSecurityDeposit >= settings.maxSecurityDeposit) {
    errors.minSecurityDeposit = 'Minimum must be less than maximum';
    errors.maxSecurityDeposit = 'Maximum must be greater than minimum';
  }

  if (settings.depositReleaseHours < 0 || settings.depositReleaseHours > 168) {
    errors.depositReleaseHours = 'Must be between 0 and 168 hours (1 week)';
  }

  // Validate booking fee
  if (settings.bookingFee.percentage < 0 || settings.bookingFee.percentage > 100) {
    errors.bookingFeePercentage = 'Must be between 0% and 100%';
  }
  if (settings.bookingFee.flatAmount < 0) {
    errors.bookingFeeFlatAmount = 'Must be a positive number';
  }

  // Validate service fees
  if (settings.serviceFees.fleetOwner < 0 || settings.serviceFees.fleetOwner > 100) {
    errors.serviceFeeFleetOwner = 'Must be between 0% and 100%';
  }
  if (settings.serviceFees.individualHost < 0 || settings.serviceFees.individualHost > 100) {
    errors.serviceFeeIndividualHost = 'Must be between 0% and 100%';
  }
  if (settings.serviceFees.driver < 0 || settings.serviceFees.driver > 100) {
    errors.serviceFeeDriver = 'Must be between 0% and 100%';
  }

  // Validate first-time user discount
  if (settings.firstTimeUserDiscount.percentage < 0 || settings.firstTimeUserDiscount.percentage > 100) {
    errors.firstTimeDiscountPercentage = 'Must be between 0% and 100%';
  }
  if (settings.firstTimeUserDiscount.flatAmount < 0) {
    errors.firstTimeDiscountFlatAmount = 'Must be a positive number';
  }

  // Validate VAT (regional configs)
  if (settings.vat && Array.isArray(settings.vat)) {
    settings.vat.forEach((config, index) => {
      if (!config.regions || config.regions.length === 0) {
        errors[`vat_${index}_regions`] = 'At least one region must be selected';
      }
      if (config.value.percentage < 0 || config.value.percentage > 100) {
        errors[`vat_${index}_percentage`] = 'Must be between 0% and 100%';
      }
    });
  }

  // Validate delivery fee (regional configs)
  if (settings.deliveryFee && Array.isArray(settings.deliveryFee)) {
    settings.deliveryFee.forEach((config, index) => {
      if (!config.regions || config.regions.length === 0) {
        errors[`deliveryFee_${index}_regions`] = 'At least one region must be selected';
      }
      if (config.value.flatAmount < 0) {
        errors[`deliveryFee_${index}_flatAmount`] = 'Must be a positive number';
      }
      if (config.value.perKmRate < 0) {
        errors[`deliveryFee_${index}_perKmRate`] = 'Must be a positive number';
      }
    });
  }

  // Validate Elite tier discounts
  ['silver', 'gold', 'platinum'].forEach(tier => {
    const discount = settings.eliteTierDiscounts[tier as keyof typeof settings.eliteTierDiscounts];
    if (discount < 0 || discount > 100) {
      errors[`elite_${tier}_discount`] = 'Must be between 0% and 100%';
    }
  });

  return errors;
}

// Default settings
export const DEFAULT_RENTAL_SETTINGS: RentalSettings = {
  enableAddressRelease: true,
  addressReleaseHours: 1
};

export const DEFAULT_PAYMENT_SETTINGS: PaymentSettings = {
  requireSecurityDeposit: true,
  securityDepositType: 'percentage',
  securityDepositPercentage: 20,
  securityDepositFlatAmount: 50000,
  minSecurityDeposit: 10000,
  maxSecurityDeposit: 200000,
  depositReleaseHours: 48,
  enableDepositNotifications: true,
  bookingFee: {
    type: 'percentage',
    percentage: 5,
    flatAmount: 2000
  },
  serviceFees: {
    fleetOwner: 10,
    individualHost: 15,
    driver: 20
  },
  firstTimeUserDiscount: {
    enabled: true,
    type: 'percentage',
    percentage: 10,
    flatAmount: 5000,
    applyTo: 'subtotal'
  },
  vat: [
    {
      regions: ['Lagos'],
      value: {
        enabled: true,
        percentage: 7.5,
        applyTo: ['bookingFee', 'rentalCost']
      }
    },
    {
      regions: ['Nairobi'],
      value: {
        enabled: true,
        percentage: 16,
        applyTo: ['bookingFee', 'rentalCost']
      }
    },
    {
      regions: ['Accra'],
      value: {
        enabled: true,
        percentage: 12.5,
        applyTo: ['bookingFee', 'rentalCost']
      }
    }
  ],
  deliveryFee: [
    {
      regions: ['All'],
      value: {
        enabled: true,
        type: 'flat',
        flatAmount: 5000,
        perKmRate: 100
      }
    }
  ],
  eliteTierDiscounts: {
    silver: 25,
    gold: 50,
    platinum: 75
  }
};

// ========================================
// BOOKING POLICIES API
// ========================================

/**
 * Get booking policies
 */
export async function getBookingPolicies(): Promise<BookingPoliciesResponse> {
  try {
    const token = localStorage.getItem('token');

    const response = await fetch(`${getApiBaseUrl()}/api/admin/settings/booking-policies`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    const result = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: result.message || 'Failed to load booking policies'
      };
    }

    return {
      success: true,
      data: result.data || result
    };
  } catch (error: any) {
    console.error('Error loading booking policies:', error);
    return {
      success: false,
      error: error.message || 'Network error loading booking policies'
    };
  }
}

/**
 * Update booking policies
 */
export async function updateBookingPolicies(policies: BookingPolicies): Promise<BookingPoliciesResponse> {
  try {
    const token = localStorage.getItem('token');

    const response = await fetch(`${getApiBaseUrl()}/api/admin/settings/booking-policies`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(policies)
    });

    const result = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: result.message || 'Failed to save booking policies'
      };
    }

    return {
      success: true,
      data: result.data || result
    };
  } catch (error: any) {
    console.error('Error saving booking policies:', error);
    return {
      success: false,
      error: error.message || 'Network error saving booking policies'
    };
  }
}

export const DEFAULT_BOOKING_POLICIES: BookingPolicies = {
  modificationFee: 2500,
  minimumModificationHours: 2,
  cancellationPolicies: {
    free: { hoursThreshold: 48, feePercentage: 0, description: 'Free cancellation' },
    low_fee: { hoursThreshold: 24, feePercentage: 10, description: 'Low fee (10%)' },
    medium_fee: { hoursThreshold: 6, feePercentage: 25, description: 'Medium fee (25%)' },
    high_fee: { hoursThreshold: 1, feePercentage: 50, description: 'High fee (50%)' },
    no_refund: { hoursThreshold: 0, feePercentage: 100, description: 'No refund' },
  }
};

export const DEFAULT_HIRE_STANDARDS: HireStandards = {
  hourly: {
    minimum_hours: 4,
    maximum_hours: 7,
    requires_working_hours: true,
    description: 'Short-term hire with specific time requirements'
  },
  daily: {
    minimum_days: 1,
    maximum_days: 14,
    requires_working_hours: false,
    description: 'Multi-day hire with flexible timing. Duration calculated by calendar days.'
  }
};

// ========================================
// HIRE STANDARDS API
// ========================================

/**
 * Get current hire duration standards
 */
export async function getHireStandards(): Promise<HireStandardsResponse> {
  try {
    const token = localStorage.getItem('token');
    const response = await fetch(`${getApiBaseUrl()}/api/v1/admin/settings/hire-standards`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    });

    const result = await response.json();

    if (!response.ok || !result.success) {
      return {
        success: false,
        error: result.message || 'Failed to fetch hire standards'
      };
    }

    return {
      success: true,
      data: result.data
    };
  } catch (error: any) {
    console.error('Error fetching hire standards:', error);
    return {
      success: false,
      error: error.message || 'Network error fetching hire standards'
    };
  }
}

/**
 * Update hire duration standards
 */
export async function updateHireStandards(settings: HireStandards): Promise<HireStandardsResponse> {
  try {
    const token = localStorage.getItem('token');
    const response = await fetch(`${getApiBaseUrl()}/api/v1/admin/settings/hire-standards`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(settings)
    });

    const result = await response.json();

    if (!response.ok || !result.success) {
      return {
        success: false,
        error: result.error?.message || result.message || 'Failed to save hire standards'
      };
    }

    return {
      success: true,
      data: result.data || result
    };
  } catch (error: any) {
    console.error('Error saving hire standards:', error);
    return {
      success: false,
      error: error.message || 'Network error saving hire standards'
    };
  }
}

// ========================================
// BOOKING APPROVAL TIMEOUT API
// ========================================

/**
 * Get booking approval timeout settings
 */
export async function getBookingApprovalTimeout(): Promise<BookingApprovalTimeoutResponse> {
  try {
    const token = localStorage.getItem('token');

    const response = await fetch(`${getApiBaseUrl()}/api/admin/settings/booking-approval-timeout`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    const result = await response.json();

    if (!response.ok) {
      // Parse structured error response
      const errorResponse: ApiErrorResponse = typeof result.error === 'object'
        ? result.error
        : { code: 'UNKNOWN_ERROR', message: result.error || result.message || 'Failed to load booking approval timeout' };

      return {
        success: false,
        error: errorResponse
      };
    }

    return {
      success: true,
      data: result.data
    };
  } catch (error: any) {
    console.error('Error loading booking approval timeout:', error);
    return {
      success: false,
      error: {
        code: 'NETWORK_ERROR',
        message: error.message || 'Network error loading booking approval timeout'
      }
    };
  }
}

/**
 * Update booking approval timeout settings (PATCH request for partial updates)
 */
export async function updateBookingApprovalTimeout(
  updates: BookingApprovalTimeoutUpdate
): Promise<BookingApprovalTimeoutResponse> {
  try {
    const token = localStorage.getItem('token');

    const response = await fetch(`${getApiBaseUrl()}/api/admin/settings/booking-approval-timeout`, {
      method: 'PATCH',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(updates)
    });

    const result = await response.json();

    if (!response.ok) {
      // Parse structured error response with field-level details
      const errorResponse: ApiErrorResponse = typeof result.error === 'object'
        ? result.error
        : { code: 'UNKNOWN_ERROR', message: result.error || result.message || 'Failed to update booking approval timeout' };

      return {
        success: false,
        error: errorResponse
      };
    }

    return {
      success: true,
      data: result.data,
      message: result.message,
      updated_fields: result.data?.updated_fields
    };
  } catch (error: any) {
    console.error('Error updating booking approval timeout:', error);
    return {
      success: false,
      error: {
        code: 'NETWORK_ERROR',
        message: error.message || 'Network error updating booking approval timeout'
      }
    };
  }
}

/**
 * Validate booking approval timeout values (in minutes)
 */
export function validateBookingApprovalTimeout(
  paidTimeoutMinutes?: number,
  unpaidTimeoutMinutes?: number
): Record<string, string> {
  const errors: Record<string, string> = {};

  if (paidTimeoutMinutes !== undefined) {
    if (!Number.isInteger(paidTimeoutMinutes)) {
      errors.paid_timeout_minutes = 'Must be a whole number';
    } else if (paidTimeoutMinutes < 0 || paidTimeoutMinutes > 4320) {
      errors.paid_timeout_minutes = 'Must be between 0 and 4320 minutes (0-72 hours)';
    }
  }

  if (unpaidTimeoutMinutes !== undefined) {
    if (!Number.isInteger(unpaidTimeoutMinutes)) {
      errors.unpaid_timeout_minutes = 'Must be a whole number';
    } else if (unpaidTimeoutMinutes < 0 || unpaidTimeoutMinutes > 10080) {
      errors.unpaid_timeout_minutes = 'Must be between 0 and 10080 minutes (0 = never expire, up to 7 days)';
    }
  }

  return errors;
}

/**
 * Helper function to convert minutes to hours display
 */
export function minutesToHoursDisplay(minutes: number): string {
  const hours = minutes / 60;
  if (hours === Math.floor(hours)) {
    return `${hours}h`;
  }
  const wholeHours = Math.floor(hours);
  const remainingMinutes = minutes % 60;
  if (wholeHours === 0) {
    return `${remainingMinutes}m`;
  }
  return `${wholeHours}h ${remainingMinutes}m`;
}

// Default booking approval timeout (in minutes)
export const DEFAULT_BOOKING_APPROVAL_TIMEOUT: BookingApprovalTimeout = {
  paid_timeout_minutes: 1440,    // 24 hours
  unpaid_timeout_minutes: 1440    // 24 hours — payment window for admin/partner bookings
};
